import { ConnectedDeviceSummary, DeviceSummary, DeviceType, GPSLocationItem } from '@types';

import { DisplayController } from './DisplayController';

// ---->>> On changes in file: COPY-PASTE FROM THIS LINE <<<----
/**
 * It is advised that all code lines below (excluding imports), should remain consistent across all
 * client-side repositories or projects (website, mobile) due to the inversion of dependencies.
 * Consequently, it is recommended to copy and paste all code, from this line to the end of the file,
 * to apply same changes on all repositories or projects.
 */

/**
 * Display of device data details
 */
interface DeviceDisplay {
	// Main
	type: string;
	// Details
	deviceDescription: string;
	make: string;
	model: string;
	serialNumber: string;
	certificationExpiryDate: string;
	size: string;
	width: string;
	widthUnit: string;
	installationDate: string;
	gpsLocation: string;
	deviceStatus: string;
}

type AnyDevice = DeviceType | DeviceSummary | ConnectedDeviceSummary;

export class DeviceDisplayController extends DisplayController<AnyDevice, DeviceDisplay> {
	protected getLabels() {
		return {
			type: '',
			deviceDescription: 'Device type',
			make: 'Make',
			model: 'Model',
			serialNumber: 'Serial number',
			certificationExpiryDate: 'Certification expiry date',
			size: 'Size',
			width: 'Width',
			widthUnit: 'Width unit',
			installationDate: 'Installation date',
			gpsLocation: 'Location',
			deviceStatus: 'Status',
		};
	}

	/**
	 * Define display rules based on Device Type and other conditions
	 * @param data
	 * @returns
	 */
	protected visibilityRules(data: DeviceType) {
		return {
			type: true,
			deviceDescription: true,
			make: true,
			model: true,
			serialNumber: true,
			certificationExpiryDate: [DeviceType.type.MODULAR_METER, DeviceType.type.SC_METER].includes(
				data.type as DeviceType.type,
			),
			size:
				![
					DeviceType.type.MODULAR_METER,
					DeviceType.type.WL_STATION,
					DeviceType.type.WEIR_DISCHARGE,
				].includes(data.type as DeviceType.type) && !this.isOpenChannelSensor(data),
			width: this.isOpenChannelSensor(data),
			widthUnit: this.isOpenChannelSensor(data),
			installationDate: true,
			gpsLocation: this.isValidLocation((data as DeviceType).gpsLocation),
			deviceStatus: true,
		};
	}

	protected transformData(data: DeviceType) {
		const defaultStr = this.defaultStr;
		return {
			type: data.type ?? defaultStr,
			deviceDescription: !this.isEmptyValue(data.deviceDescription)
				? this.toSentenceCase(data.deviceDescription ?? '')
				: defaultStr,
			make: data.make ?? defaultStr,
			model: data.model ?? defaultStr,
			serialNumber: data.serialNumber ?? defaultStr,
			certificationExpiryDate: !this.isEmptyDate(data.certificationExpiryDate)
				? this.convertTimeToDisplayFormat(data.certificationExpiryDate ?? '')
				: defaultStr,
			size: Number.isFinite(data.size) ? String(data.size) : defaultStr,
			width: Number.isFinite(data.width) ? String(data.width) : defaultStr,
			widthUnit: data.widthUnits ?? defaultStr,
			installationDate: !this.isEmptyDate(data.installationDate)
				? this.convertTimeToDisplayFormat(data.installationDate)
				: defaultStr,
			gpsLocation: data.gpsLocation ? this.transformGpsLocation(data.gpsLocation) : defaultStr,
			deviceStatus: !this.isEmptyValue(data.deviceStatus)
				? this.toSentenceCase(data.deviceStatus as string)
				: defaultStr,
		};
	}

	private isOpenChannelSensor(data: DeviceType) {
		const isSensor = data.type === DeviceType.type.SENSOR;
		const isOpenChannel = data.sizeType === DeviceType.sizeType.OPEN_CHANNEL;
		return isSensor && isOpenChannel;
	}

	/**
	 * Checks if the provided GPS location is valid.
	 * A valid location must have non-zero latitude or longitude and both must be finite numbers.
	 * @param {GPSLocationItem} [gpsLocation] - The GPS location object containing latitude and longitude.
	 * @returns {boolean} True if the GPS location is valid, otherwise false.
	 */
	private isValidLocation(gpsLocation?: GPSLocationItem) {
		const result =
			gpsLocation &&
			!this.isEmptyValue(gpsLocation) &&
			(gpsLocation.latitude != 0 || gpsLocation.longitude) != 0 &&
			(Number.isFinite(gpsLocation.latitude) || Number.isFinite(gpsLocation.longitude));
		return Boolean(result);
	}

	/**
	 * Formats GPS coordinates into a string.
	 * @param {GPSLocationItem} gpsLocation - The GPS location object containing latitude and longitude.
	 * @param {string} [separator=','] - The separator to use between latitude and longitude in the output string.
	 * @returns {string} A formatted string representing the GPS coordinates, or the default string if the input is invalid.
	 */
	private transformGpsLocation(gpsLocation: GPSLocationItem, separator = ',') {
		if (!gpsLocation || (!gpsLocation.latitude && !gpsLocation.longitude)) {
			return this.defaultStr;
		}

		const { latitude, longitude } = gpsLocation;
		const latitudeStr = latitude && Number.isFinite(latitude) ? latitude.toFixed(5) : '';
		const longitudeStr = longitude && Number.isFinite(longitude) ? longitude.toFixed(5) : '';

		return `${latitudeStr}${separator}${longitudeStr}`;
	}
}
